/**
 * The top of the Rooms list: the Now band (what is live, then what is up next) and the Quick lives strip of call
 * tiles. The computer's page and Season's share them.
 */
import { useNavigate } from "react-router";
import type { WebinarSummary } from "../../../shared/types";
import { studioPath } from "../../board/LiveStrip";
import { Button } from "../../components/ui/Button";
import { FaceStack } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import "../../styles/room-live.css";
import { LivePicture } from "./LivePicture";
import { callWhen, inRoomLine, isCall, ranWords, tileStatus, untilWords } from "./quickWords";
import { callPath, roomTarget } from "./roomWords";

const photoFaces = (room: WebinarSummary) => room.faces.map((f) => ({ name: f.name, photo: f.avatar_url }));

function LiveNowCard({ room }: { room: WebinarSummary }) {
  const navigate = useNavigate();
  const live = room.live;
  if (!live) return null;
  return (
    <section className="nb nb-live" aria-label={`Live now: ${room.title}`}>
      <div className="nb-glow" aria-hidden="true" />
      <div className="nb-top">
        <span className="nb-pulse">
          <i />
          Live now
        </span>
        <span className="nb-meta">{ranWords(live, Date.now())}</span>
      </div>
      <h2>{room.title}</h2>
      <div className="nb-who">
        <FaceStack people={photoFaces(room)} size={34} />
        <span>{inRoomLine(live.cameLive)}</span>
      </div>
      <div className="nb-act">
        <Button variant="p" icon="live" onClick={() => navigate(studioPath(room.id))}>
          Back to the studio
        </Button>
        {isCall(room) ? null : (
          <button type="button" className="nb-link" onClick={() => navigate(roomTarget(room))}>
            Open its page <Icon name="right" />
          </button>
        )}
      </div>
    </section>
  );
}

function UpNextCard({ room }: { room: WebinarSummary }) {
  const navigate = useNavigate();
  const live = room.live;
  if (!live) return null;
  const at = new Date(live.startsAt);
  const day = at.toLocaleDateString("en-US", { weekday: "long", timeZone: live.timezone });
  const time = at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: live.timezone });
  const seats = live.series?.seats ?? room.registered;
  return (
    <section className="nb nb-next" aria-label={`Up next: ${room.title}`}>
      {room.poster_url ? <img className="nb-bg" src={room.poster_url} alt="" /> : null}
      <div className="nb-shade" aria-hidden="true" />
      <div className="nb-top">
        <span className="nb-chip">Up next · {untilWords(live.startsAt, Date.now())}</span>
        {live.series ? <span className="nb-meta">Session {live.series.done + 1}</span> : null}
      </div>
      <h2>{room.title}</h2>
      <p className="nb-when">
        {day}, {time}
      </p>
      <div className="nb-who">
        <FaceStack people={photoFaces(room)} size={34} more={Math.max(0, seats - room.faces.length)} />
        <span>{seats.toLocaleString("en-US")} signed up so far</span>
      </div>
      <div className="nb-act">
        <Button onClick={() => navigate(studioPath(room.id))}>Open the studio</Button>
        <button type="button" className="nb-link light" onClick={() => navigate(roomTarget(room))}>
          Open the room <Icon name="right" />
        </button>
      </div>
    </section>
  );
}

/** What is live and what is next; nothing at all when neither. */
export function NowBand({ live, next }: { live: readonly WebinarSummary[]; next: WebinarSummary | null }) {
  if (live.length === 0 && !next) return null;
  return (
    <div className="nb-row">
      {live.map((room) => (
        <LiveNowCard key={room.id} room={room} />
      ))}
      {next ? <UpNextCard room={next} /> : null}
    </div>
  );
}

function CallTile({ room }: { room: WebinarSummary }) {
  const navigate = useNavigate();
  const live = room.live;
  if (!live) return null;
  const status = tileStatus(live);
  const ran = ranWords(live, Date.now());
  return (
    <button type="button" className="ct" data-opens={`webinar:${room.id}`} onClick={() => navigate(callPath(room.id))}>
      <span className="ct-pic">
        <LivePicture poster={room.poster_url} title={room.title} faces={room.faces} />
        {status.text === "Replay ready" ? (
          <span className="ct-play">
            <Icon name="play" />
          </span>
        ) : null}
        <span className={status.tone ? `ct-chip ${status.tone}` : "ct-chip"}>
          {status.tone === "live" ? <i /> : null}
          {status.text}
        </span>
      </span>
      <span className="ct-t">
        <b>{room.title}</b>
        <span>{[callWhen(live.startsAt, live.timezone, Date.now()), ran, `${live.cameLive.toLocaleString("en-US")} came`].filter(Boolean).join(" · ")}</span>
      </span>
    </button>
  );
}

/** Every Go live now, newest first, as tiles to flick through. */
export function QuickStrip({ calls }: { calls: readonly WebinarSummary[] }) {
  if (calls.length === 0) return null;
  return (
    <section className="rq" aria-label="Quick lives">
      <div className="rq-h">
        <div>
          <h2>Quick lives</h2>
          <p>Every Go live now, newest first. Each has its own link, replay and follow-ups.</p>
        </div>
      </div>
      <div className="rq-strip">
        {calls.map((c) => (
          <CallTile key={c.id} room={c} />
        ))}
      </div>
    </section>
  );
}
